/**
 * Floating Chat Logic Hook
 * Separates business logic from state management for chat functionality
 */

import { useCallback, useMemo, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logComponent } from '@/services/loggingService';
import {
  FloatingChatState,
  FloatingChatAction,
  FilterCounts,
  UserRoles
} from './useFloatingChatState';
import { saveChatStateSafe } from '@/utils/chatStatePersistence';
import { chatToastCoordinator } from '@/services/ChatToastCoordinator';

interface ApplicationGroup {
  id: string;
  job_id: string;
  job_title: string;
  company_name: string;
  company_logo_url?: string;
  conversations: any[];
  mostRecentMessage: string;
  mostRecentMessageAt: string;
  totalUnreadCount: number;
  applicantCount: number;
}

interface UseFloatingChatLogicProps {
  state: FloatingChatState;
  dispatch: React.Dispatch<FloatingChatAction>;
  conversations: any[];
  applicationGroups: ApplicationGroup[];
  isUserOnline: (userId: string) => boolean;
  fetchMessages: (conversationId: string) => void;
  subscribeToConversation: (conversationId: string) => void;
  markAsRead: (conversationId: string) => void;
  userId?: string;
}

export function useFloatingChatLogic({ 
  state, 
  dispatch, 
  conversations,
  applicationGroups,
  isUserOnline,
  fetchMessages,
  subscribeToConversation,
  markAsRead,
  userId
}: UseFloatingChatLogicProps) {
  // Track when user explicitly closes the last chat window
  const lastActionWasCloseAllRef = useRef(false);
  
  // Persist state whenever it changes
  useEffect(() => {
    if (!userId) return;
    
    // Debounce to avoid excessive writes
    const timeoutId = setTimeout(() => {
      const statesMap = new Map(
        Object.entries(state.activeChatStates.states).map(([id, s]) => [
          id,
          { isMinimized: s.isMinimized, isExpanded: s.isExpanded }
        ])
      );
      
      const chatCount = state.activeChatStates.order.length;
      
      // Use safe-save with explicit empty allowance flag
      saveChatStateSafe(userId, statesMap, state.activeChatStates.order, {
        allowEmpty: lastActionWasCloseAllRef.current
      });
      
      // Reset flag after persisting intended empty state
      if (chatCount === 0 && lastActionWasCloseAllRef.current) {
        lastActionWasCloseAllRef.current = false;
      }
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [userId, state.activeChatStates.states, state.activeChatStates.order]);

  // Fetch user roles and companies
  const fetchUserRoles = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Use the session's user instead of getUser() to ensure auth context is correct
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      // Get user's company memberships with company names
      const { data: companyMemberships, error: companyError } = await supabase
        .from('company_team_members')
        .select(`
          company_id,
          member_role,
          company_profiles (
            id,
            name
          )
        `)
        .eq('user_id', user.id as any);

      if (companyError) throw companyError;

      // Get user roles from profile and company memberships
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_type')
        .eq('user_id', user.id as any)
        .maybeSingle();

      if (profileError) throw profileError;

      // Check for internal portal access
      const { data: internalData } = await supabase
        .from('internal_portal_users')
        .select('member_role')
        .eq('user_id', user.id as any)
        .maybeSingle();

      const companies = (companyMemberships || []).map((membership: any) => ({
        id: (membership as any).company_id,
        name: ((membership as any).company_profiles as any)?.name || 'Unknown Company'
      }));

      // Combine company roles with user type
      const companyRoles = companyMemberships?.map((m: any) => (m as any).member_role || 'member') || [];
      const userTypeRoles = (profile as any)?.user_type ? [(profile as any).user_type] : ['talent'];
      const internalRoles = (internalData as any)?.member_role ? [`internal_${(internalData as any).member_role}`] : [];
      const roles = [...companyRoles, ...userTypeRoles, ...internalRoles, 'company'];

      const userRoles: UserRoles = {
        companies,
        roles: [...new Set(roles)] // Remove duplicates
      };

      dispatch({ type: 'SET_USER_ROLES', payload: userRoles });
    } catch (error) {
      logComponent('FloatingChatLogic').error('Failed to fetch user roles', error as Error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load user roles' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  // Fetch participant roles for conversations - supports multiple roles per user per job
  const fetchParticipantRoles = useCallback(async () => {
    try {
      // Use session user to ensure consistent auth context
      const { data: { session } } = await supabase.auth.getSession();
      const currentUserId = session?.user?.id;
      if (!currentUserId) return;

      // Check for internal portal access
      const { data: internalData } = await supabase
        .from('internal_portal_users')
        .select('member_role')
        .eq('user_id', currentUserId as any)
        .maybeSingle();
      
      const hasInternalAccess = !!internalData;

      const rolePromises = conversations.map(async (conv) => {
        try {
          const userRoles = [];

          // If user has internal access, add 'internal' role
          if (hasInternalAccess) {
            userRoles.push('internal');
          }

          // Check if this is a hiring/application conversation
          const isHiringConversation = conv.conversation_category === 'hiring' || 
                                       conv.conversation_category === 'hiring_application' ||
                                       conv.conversation_category === 'application' ||
                                       conv.conversation_type === 'application' ||
                                       !!conv.job_application_id;

          if (isHiringConversation && conv.job_application_id) {
            // Query to get application and job details
            const { data: applicationData, error: appError } = await supabase
              .from('company_job_applications')
              .select(`
                candidate_id,
                job_id,
                company_job_postings!inner(
                  user_id,
                  company_id,
                  title,
                  company_profiles!inner(name)
                )
              `)
              .eq('id', conv.job_application_id)
              .maybeSingle();

            if (!appError && applicationData) {
              // Map candidate role to 'talent' for UI filter compatibility
              if ((applicationData as any).candidate_id === currentUserId) {
                userRoles.push('talent');
              }
              
              // Map job_owner role to 'recruiter' for UI filter compatibility
              if ((applicationData as any).company_job_postings.user_id === currentUserId) {
                userRoles.push('recruiter');
              }
              
              // Map company_member role to 'recruiter' for UI filter compatibility
              const { data: teamMember } = await supabase
                .from('company_team_members')
                .select('member_role')
                .eq('company_id', (applicationData as any).company_job_postings.company_id as any)
                .eq('user_id', currentUserId as any)
                .maybeSingle();

              if (teamMember && !userRoles.includes('recruiter')) {
                userRoles.push('recruiter');
              }
            }
          } else {
            // For non-hiring conversations, assign roles based on user type
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('user_type')
              .eq('user_id', currentUserId as any)
              .maybeSingle();
            
            if (profile) {
              if ((profile as any).user_type === 'business') {
                userRoles.push('recruiter');
              } else if ((profile as any).user_type === 'talent') {
                userRoles.push('talent');
              }
            }
          }

          // Default to general if no specific roles found
          if (userRoles.length === 0) {
            userRoles.push('general');
          }

          return { [conv.id]: userRoles };
        } catch (error) {
          logComponent('FloatingChatLogic').warn('Failed to fetch roles for conversation', {
            conversationId: conv.id,
            error
          });
          return { [conv.id]: ['general'] };
        }
      });

      const roleResults = await Promise.all(rolePromises);
      const participantRoles = roleResults.reduce((acc, result) => ({ ...acc, ...result }), {});
      
      dispatch({ type: 'SET_PARTICIPANT_ROLES', payload: participantRoles });
    } catch (error) {
      logComponent('FloatingChatLogic').error('Failed to fetch participant roles', error as Error);
    }
  }, [conversations]);

  // Filter conversations based on active filters
  const filteredConversations = useMemo(() => {
    const filtered = conversations.filter(conv => {
      // Role filter
      if (!state.activeRoleFilters.has('all')) {
        const roles = state.participantRoles[conv.id] || [];
        const hasMatchingRole = Array.from(state.activeRoleFilters).some(filter => {
          // Handle company-specific filters
          if (filter.startsWith('company-')) {
            const companyId = filter.replace('company-', '');
            return conv.company_id === companyId || roles.includes('company');
          }
          return roles.includes(filter);
        });
        if (!hasMatchingRole) return false;
      }

      // Type filter  
      if (!state.activeTypeFilters.has('all')) {
        const hasMatchingType = Array.from(state.activeTypeFilters).some(filter => {
          if (filter === 'channel') return conv.access_level === 'public';
          if (filter === 'public') return conv.access_level === 'public';
          if (filter === 'hiring' || filter === 'support' || filter === 'general') {
            // Support both 'hiring' and 'hiring_application' for hiring filter
            if (filter === 'hiring') {
              return conv.conversation_category === 'hiring' || 
                     conv.conversation_category === 'hiring_application';
            }
            return conv.conversation_category === filter;
          }
          return conv.conversation_type === filter;
        });
        if (!hasMatchingType) return false;
      }
      
      return true;
    });

    // Sort by latest message time (fallback to updated_at/created_at)
    return filtered.sort((a, b) => {
      const aTime = new Date(a.last_message_at || a.updated_at || a.created_at).getTime();
      const bTime = new Date(b.last_message_at || b.updated_at || b.created_at).getTime();
      return bTime - aTime;
    });
  }, [conversations, state.activeRoleFilters, state.activeTypeFilters, state.participantRoles]);

  // Calculate conversation counts for filter badges
  const conversationCounts = useMemo((): FilterCounts => {
    const roleCounts = {
      all: conversations.length,
      talent: 0,
      company: 0,
      recruiter: 0,
      internal: 0
    };
    
    const typeCounts = {
      all: conversations.length,
      direct: 0,
      group: 0,
      channel: 0
    };

    conversations.forEach(conv => {
      // Count by participant roles
      const roles = state.participantRoles[conv.id] || [];
      roles.forEach(role => {
        if (role in roleCounts) {
          roleCounts[role as keyof typeof roleCounts]++;
        }
      });

      // Count by conversation types
      if (conv.conversation_type === 'direct') {
        typeCounts.direct++;
      } else if (conv.conversation_type === 'group') {
        typeCounts.group++;
      } else if (conv.access_level === 'public') {
        typeCounts.channel++;
      }
    });

    return { roleCounts, typeCounts };
  }, [conversations, state.participantRoles]);

  // Chat window management
  const openChatWindow = useCallback((conversationId: string, conversationData?: any): boolean => {
    // Check if conversation exists
    const conversation = conversations.find(c => c.id === conversationId) || conversationData;
    if (!conversation) {
      logComponent('FloatingChatLogic').warn('Attempted to open non-existent conversation', {
        conversationId
      });
      return false;
    }

    // Clear any pending toasts for this conversation
    chatToastCoordinator.clearToast(conversationId);
    
    // Fetch messages when opening chat window
    fetchMessages(conversationId);
    
    // Subscribe to real-time updates for this conversation
    subscribeToConversation(conversationId);

    dispatch({ 
      type: 'OPEN_CHAT_WINDOW', 
      payload: { conversationId } 
    });
    
    // ✅ Only mark as read if window is NOT minimized
    // Check state after dispatch to get the updated state
    const willBeMinimized = state.activeChatStates.states[conversationId]?.isMinimized;
    if (!willBeMinimized) {
      markAsRead(conversationId);
    }
    
    return true;
  }, [conversations, dispatch, fetchMessages, subscribeToConversation, markAsRead, state.activeChatStates.states]);

  const closeChatWindow = useCallback((conversationId: string) => {
    // Check if this is the last open chat window
    const isLast = state.activeChatStates.order.length === 1 && 
                   state.activeChatStates.order[0] === conversationId;
    
    if (isLast) {
      lastActionWasCloseAllRef.current = true;
    }
    
    dispatch({ type: 'CLOSE_CHAT_WINDOW', payload: conversationId });
  }, [dispatch, state.activeChatStates.order]);

  const minimizeChatWindow = useCallback((conversationId: string) => {
    dispatch({ type: 'MINIMIZE_CHAT_WINDOW', payload: conversationId });
  }, [dispatch]);

  const maximizeChatWindow = useCallback((conversationId: string) => {
    // ✅ Mark as read when window is restored/maximized
    markAsRead(conversationId);
    dispatch({ type: 'MAXIMIZE_CHAT_WINDOW', payload: conversationId });
  }, [dispatch, markAsRead]);

  const expandChatWindow = useCallback((conversationId: string) => {
    dispatch({ type: 'UPDATE_CHAT_STATE', payload: { conversationId, updates: { isExpanded: true, isMinimized: false } } });
  }, [dispatch]);

  const collapseChatWindow = useCallback((conversationId: string) => {
    dispatch({ type: 'UPDATE_CHAT_STATE', payload: { conversationId, updates: { isExpanded: false } } });
  }, [dispatch]);

  const moveToFirstPosition = useCallback((conversationId: string) => {
    dispatch({ type: 'MOVE_TO_FIRST_POSITION', payload: conversationId });
  }, [dispatch]);

  // Filter management
  const toggleRoleFilter = useCallback((role: string) => {
    dispatch({ type: 'TOGGLE_ROLE_FILTER', payload: role });
  }, [dispatch]);

  const toggleTypeFilter = useCallback((type: string) => {
    dispatch({ type: 'TOGGLE_TYPE_FILTER', payload: type });
  }, [dispatch]);

  const resetFilters = useCallback(() => {
    dispatch({ type: 'RESET_FILTERS' });
  }, [dispatch]);

  // Online status management
  const updateOnlineStatus = useCallback((userId: string, isOnline: boolean) => {
    if (isOnline) {
      dispatch({ type: 'ADD_ONLINE_USER', payload: userId });
    } else {
      dispatch({ type: 'REMOVE_ONLINE_USER', payload: userId });
    }
  }, [dispatch]);

  // Helper functions
  const isConversationOnline = useCallback((conversation: any) => {
    if (conversation.created_by && isUserOnline(conversation.created_by)) {
      return true;
    }
    return false;
  }, [isUserOnline]);

  const isFirstPositionChat = useCallback((conversationId: string) => {
    return state.activeChatStates.order[0] === conversationId;
  }, [state.activeChatStates.order]);

  const toggleGroupExpansion = useCallback((groupId: string) => {
    dispatch({ type: 'TOGGLE_GROUP_EXPANSION', payload: groupId });
  }, [dispatch]);

  const getFilterButtonText = useCallback(() => {
    const roleCount = state.activeRoleFilters.size;
    const typeCount = state.activeTypeFilters.size;
    
    if (state.activeRoleFilters.has('all') && state.activeTypeFilters.has('all')) {
      return 'All Chats';
    }
    
    const parts = [];
    if (!state.activeRoleFilters.has('all')) {
      parts.push(`${roleCount} Role${roleCount > 1 ? 's' : ''}`);
    }
    if (!state.activeTypeFilters.has('all')) {
      parts.push(`${typeCount} Type${typeCount > 1 ? 's' : ''}`);
    }
    
    return parts.join(', ');
  }, [state.activeRoleFilters, state.activeTypeFilters]);

  // Filter and group conversations with role-based group display  
  const filteredData = useMemo(() => {
    // Preserve current expansion state before filtering
    const currentExpandedGroups = new Set(state.expandedGroups);
    
    // Filter conversations based on active filters
    let filtered = conversations.filter(conv => {
      // Role filter
      if (!state.activeRoleFilters.has('all')) {
        const convRoles = state.participantRoles[conv.id] || ['general'];
        const hasMatchingRole = convRoles.some(role => state.activeRoleFilters.has(role));
        if (!hasMatchingRole) return false;
      }
      
      // Type filter
      if (!state.activeTypeFilters.has('all')) {
        if (!state.activeTypeFilters.has(conv.conversation_type)) return false;
      }
      
      return true;
    });

    // Separate conversations by user's role in each specific conversation
    const hirerConversations: any[] = [];
    const candidateConversations: any[] = [];
    const otherConversations: any[] = [];
    
    filtered.forEach(conv => {
      const roles = state.participantRoles[conv.id] || [];
      const isHiringConv = conv.conversation_type === 'application' || 
                          conv.conversation_category === 'application' ||
                          conv.conversation_category === 'hiring';
      
      if (isHiringConv) {
        const isHirer = roles.includes('job_owner') || roles.includes('company_member');
        const isCandidate = roles.includes('candidate');
        
        // Prioritize conversation-specific role detection over role arrays
        // Use the isCandidate flag set during conversation processing for accuracy
        if (conv.isCandidate === true) {
          // This conversation is for user's own application - show individually
          candidateConversations.push(conv);
        } else if (conv.isCandidate === false || isHirer) {
          // This conversation is for viewing other candidates - show in groups
          hirerConversations.push(conv);
        } else if (isCandidate && !isHirer) {
          // Fallback: if no isCandidate flag but roles indicate candidate
          candidateConversations.push(conv);
        } else {
          otherConversations.push(conv);
        }
      } else {
        otherConversations.push(conv);
      }
    });
    
    // Only show grouped view for conversations where user is acting as hirer
    if (hirerConversations.length > 0) {
      // Get application groups only for hirer conversations
      const hirerConvIds = new Set(hirerConversations.map(c => c.id));
      const hirerGroups = applicationGroups.filter(group => 
        group.conversations.some((conv: any) => hirerConvIds.has(conv.id))
      );

      // Create time-sorted items array
      const items = [
        // Groups for hirer conversations only
        ...hirerGroups.map(group => ({
          type: 'group',
          group,
          timestamp: group.mostRecentMessageAt
        })),
        // Individual conversations for candidate role and other conversations
        ...candidateConversations.map(conv => ({
          type: 'conversation',
          conversation: conv,
          timestamp: conv.last_message_at
        })),
        ...otherConversations.map(conv => ({
          type: 'conversation',
          conversation: conv,
          timestamp: conv.last_message_at
        }))
      ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      // Note: Group expansion state is managed externally to avoid render loops

      return {
        conversations: [...candidateConversations, ...otherConversations],
        groups: hirerGroups,
        items
      };
    }

    // For non-company users, show individual conversations
    return {
      conversations: filtered,
      groups: [],
      items: filtered.map(conv => ({
        type: 'conversation',
        conversation: conv,
        timestamp: conv.last_message_at
      })).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    };
  }, [
    conversations, 
    applicationGroups, 
    state.activeRoleFilters, 
    state.activeTypeFilters, 
    state.participantRoles, 
    state.userRoles.companies,
    state.expandedGroups
  ]);

  return {
    // Data fetching
    fetchUserRoles,
    fetchParticipantRoles,
    
    // Computed state
    filteredConversations,
    filteredData,
    conversationCounts,
    
    // Chat window management
    openChatWindow,
    closeChatWindow,
    minimizeChatWindow,
    maximizeChatWindow,
    expandChatWindow,
    collapseChatWindow,
    moveToFirstPosition,
    
    // Filter management
    toggleRoleFilter,
    toggleTypeFilter,
    resetFilters,
    
    // Group management
    toggleGroupExpansion,
    
    // Online status
    updateOnlineStatus,
    
    // Helpers
    isConversationOnline,
    isFirstPositionChat,
    getFilterButtonText,
  };
}